/**
 * Two-layer authored-vocabulary merging (decision D16) — the one place base
 * and game vocabularies meet.
 *
 * @remarks
 * A game pack EXTENDS a reference base vocabulary (e.g. the
 * standard-english-vocabulary pack in `@orpheus/content-packs`). The merge is
 * wholesale and deterministic, never partial, so an author can always answer
 * "where did this token come from?" with one rule:
 *
 * - An overlay intent or direction that re-declares a base `canonicalToken`
 *   REPLACES that base entry in place — the whole entry, aliases and
 *   bindings included; nothing is blended.
 * - Overlay entries with new canonical tokens append after the base list, in
 *   overlay order.
 * - `stopwords` union: base order first, then overlay additions, de-duplicated.
 * - `movementIntentToken`: the overlay's wins when declared, else the base's.
 * - **A pack's word outranks the base's (D226).** A base entry the overlay
 *   does not re-declare still yields any ALIAS an overlay entry of the same
 *   list spells — as its own alias or as its canonical token. Ferryman's
 *   Coin aliases `speak` onto its `say`; when the base taught `ask` the word
 *   `speak` (E10), the pack's reading had to win, and before this rule the
 *   two claims met in `compileLexicon` as a collision and the pack would not
 *   load. A base vocabulary that cannot grow a word without breaking the
 *   packs built on it is not a base. The yield is reported
 *   ({@link findBaseAliasYields}), never silent.
 * - **A pack's word outranks the base's NAME too (D235).** When an overlay
 *   entry spells a base entry's CANONICAL token as a word of its own — Salt
 *   Debt and Fever Ground alias `give` onto their `use`, and the base
 *   learned a `give` of its own when gifts to a body arrived — the base
 *   entry yields WHOLE: it leaves the merged list, its name and every alias
 *   with it, because an entry cannot stand without its name and the pack's
 *   reading of the word was there first. Those packs play exactly as they
 *   did before the base knew the verb: `give` means `use`, and `hand` (the
 *   base entry's alias) is the unknown word it always was there. Reported
 *   ({@link findBaseEntryYields}), never silent, like the alias yield — and
 *   kept apart from it, because the consequence is larger: a whole verb is
 *   absent from the game. D226 recorded the canonical-token case as "still
 *   the collision it always was"; that rule held until the base grew a verb
 *   two shipped packs had already spelled, which is the same lesson D226
 *   taught about aliases, one level up — a base vocabulary that cannot grow
 *   a verb without breaking the packs built on it is not a base.
 * - There are no removal semantics — D16 defines extend + override only.
 *
 * D137 (Calder's 2.1): the wholesale rule collides with the COMMON authoring
 * intent — an overlay re-declaring `out` with `["back", "return"]` means to
 * ADD two words and has in fact deleted `outside`, and nothing could see it:
 * the merge is valid, the pack is valid, the audit is clean, and a player
 * types the missing word. The rule stands (one rule, always answerable);
 * {@link findOverrideAliasDrops} makes the drop a thing an author DOES rather
 * than a thing that happens to them, by naming every base alias an override
 * left behind. It reads the same two lists the merge reads and stays beside
 * it, so the two cannot disagree about what "re-declares" means.
 *
 * The merge is pure data plumbing, not a validator: `compileLexicon` in
 * `@orpheus/parser-engine` remains the single semantic authority (duplicate
 * and collision detection, single-word constraints, `movementIntentToken`
 * reference — decision D14). Layer-internal duplicate canonical tokens are
 * therefore passed THROUGH deterministically rather than silently collapsed,
 * so compilation still sees — and rejects — the authoring error.
 *
 * @packageDocumentation
 */

import type { DirectionDefinition, IntentDefinition, LexiconDefinition } from '@orpheus/content-schema';

/** What merging reads of an entry: the canonical token wholesale override keys on, and the aliases (D137's drops, D226's yields). */
interface AliasedDefinition {
	readonly aliases: readonly string[];
	readonly canonicalToken: string;
}

/**
 * One base alias set an overlay override left behind (D137). Not a failure:
 * dropping an alias deliberately is legitimate, and the merge has no way to
 * tell intent from accident — so it reports, and the author decides.
 */
export interface OverrideAliasDrop {
	/** The re-declared token. */
	readonly canonicalToken: string;
	/** Every base alias the overlay entry does not carry, in base order. */
	readonly droppedAliases: readonly string[];
	/** Which list the token lives in. */
	readonly kind: 'direction' | 'intent';
}

/**
 * The base aliases each overlay override drops, for one definition list.
 *
 * @param kind - Which list is being compared
 * @param baseDefinitions - The base layer's list
 * @param overlayDefinitions - The overlay layer's list
 * @returns One drop per re-declared token that leaves base aliases behind, in overlay order
 */
const findListAliasDrops = <Definition extends AliasedDefinition>(
	kind: OverrideAliasDrop['kind'],
	baseDefinitions: readonly Definition[],
	overlayDefinitions: readonly Definition[],
): readonly OverrideAliasDrop[] => {
	const baseByToken = new Map(baseDefinitions.map((definition) => [definition.canonicalToken, definition]));

	return overlayDefinitions.flatMap((overlayDefinition) => {
		const baseDefinition = baseByToken.get(overlayDefinition.canonicalToken);

		if (baseDefinition === undefined) {
			return [];
		}

		const keptAliases = new Set(overlayDefinition.aliases);
		const droppedAliases = baseDefinition.aliases.filter((alias) => !keptAliases.has(alias));

		return droppedAliases.length === 0 ? [] : [{ canonicalToken: overlayDefinition.canonicalToken, droppedAliases, kind }];
	});
};

/**
 * One base word that YIELDED to the overlay (D226): a base entry the overlay
 * did not re-declare answered to it, and an overlay entry of the same list
 * spells it — as an alias, or as its own canonical token — so the pack's
 * reading wins and the base entry no longer answers to the word. Reported,
 * not refused: the author may not know the base ever had the word.
 */
export interface BaseAliasYield {
	/** The base entry that answered to the word. */
	readonly baseToken: string;
	/** The overlay entry the word belongs to now. */
	readonly claimingToken: string;
	/** Which list the two live in. */
	readonly kind: 'direction' | 'intent';
	/** The word itself. */
	readonly word: string;
}

/**
 * One base ENTRY that yielded whole to the overlay (D235): an overlay entry
 * spells the base entry's canonical token as a word of its own, so the base
 * entry — its name and every alias — is left out of the merge. Reported, not
 * refused: the pack's reading of the word was there first, and a verb the
 * base learned later must not break it.
 */
export interface BaseEntryYield {
	/** The base entry that is gone from this game's vocabulary. */
	readonly baseToken: string;
	/** The overlay entry whose word the base's name is now. */
	readonly claimingToken: string;
	/** Which list the two live in. */
	readonly kind: 'direction' | 'intent';
	/** The base entry's aliases that went with it — words this game no longer knows, unless another entry spells them. */
	readonly lostAliases: readonly string[];
}

/**
 * The word each overlay entry of a list spells, mapped to the entry's token
 * — the one reading both yield rules make.
 *
 * @param overlayDefinitions - The overlay layer's list
 * @returns Each spelled word → the canonical token of the entry spelling it
 */
const claimingTokensByWord = (overlayDefinitions: readonly AliasedDefinition[]): ReadonlyMap<string, string> =>
	new Map(
		overlayDefinitions.flatMap((definition) =>
			[definition.canonicalToken, ...definition.aliases].map((word): [string, string] => [word, definition.canonicalToken]),
		),
	);

/**
 * The base entries an overlay takes WHOLE (D235), for one list: every base
 * entry the overlay does not re-declare whose canonical token an overlay
 * entry spells as a word.
 *
 * @param kind - Which list is being compared
 * @param baseDefinitions - The base layer's list
 * @param overlayDefinitions - The overlay layer's list
 * @returns One yield per entry, in base order
 */
const findListEntryYields = <Definition extends AliasedDefinition>(
	kind: BaseEntryYield['kind'],
	baseDefinitions: readonly Definition[],
	overlayDefinitions: readonly Definition[],
): readonly BaseEntryYield[] => {
	const claimingTokenByWord = claimingTokensByWord(overlayDefinitions);
	const overlaidCanonicalTokens = new Set(overlayDefinitions.map((definition) => definition.canonicalToken));

	return baseDefinitions.flatMap((baseDefinition) => {
		const claimingToken = claimingTokenByWord.get(baseDefinition.canonicalToken);

		return overlaidCanonicalTokens.has(baseDefinition.canonicalToken) || claimingToken === undefined
			? []
			: [{ baseToken: baseDefinition.canonicalToken, claimingToken, kind, lostAliases: baseDefinition.aliases }];
	});
};

/**
 * Every base entry a game overlay takes whole (D235) — see
 * {@link BaseEntryYield}. Empty when no overlay word is a base entry's name.
 *
 * @param baseLexicon - The reference base vocabulary
 * @param overlayLexicon - The game's own vocabulary
 * @returns The yields, directions first, each in base order
 */
export const findBaseEntryYields = (baseLexicon: LexiconDefinition, overlayLexicon: LexiconDefinition): readonly BaseEntryYield[] => [
	...findListEntryYields('direction', baseLexicon.directions, overlayLexicon.directions),
	...findListEntryYields('intent', baseLexicon.intents, overlayLexicon.intents),
];

/**
 * The base words that yield to an overlay's words (D226), for one list — of
 * the entries that stand; an entry that yields whole (D235) is reported once,
 * as itself, and not again alias by alias.
 *
 * @param kind - Which list is being compared
 * @param baseDefinitions - The base layer's list
 * @param overlayDefinitions - The overlay layer's list
 * @returns One yield per word, in base order
 */
const findListAliasYields = <Definition extends AliasedDefinition>(
	kind: BaseAliasYield['kind'],
	baseDefinitions: readonly Definition[],
	overlayDefinitions: readonly Definition[],
): readonly BaseAliasYield[] => {
	const claimingTokenByWord = claimingTokensByWord(overlayDefinitions);
	const overlaidCanonicalTokens = new Set(overlayDefinitions.map((definition) => definition.canonicalToken));

	return baseDefinitions.flatMap((baseDefinition) =>
		overlaidCanonicalTokens.has(baseDefinition.canonicalToken) || claimingTokenByWord.has(baseDefinition.canonicalToken)
			? []
			: baseDefinition.aliases.flatMap((word) => {
				const claimingToken = claimingTokenByWord.get(word);

				return claimingToken === undefined ? [] : [{ baseToken: baseDefinition.canonicalToken, claimingToken, kind, word }];
			}),
	);
};

/**
 * Every base word a game overlay takes for itself (D226) — the merge's
 * second companion, reading the two layers under the yield rule the merge
 * applies. Empty when the overlay spells no word the base gives another
 * entry.
 *
 * @param baseLexicon - The reference base vocabulary
 * @param overlayLexicon - The game's own vocabulary
 * @returns The yields, directions first, each in base order
 */
export const findBaseAliasYields = (baseLexicon: LexiconDefinition, overlayLexicon: LexiconDefinition): readonly BaseAliasYield[] => [
	...findListAliasYields('direction', baseLexicon.directions, overlayLexicon.directions),
	...findListAliasYields('intent', baseLexicon.intents, overlayLexicon.intents),
];

/**
 * Every word one list's entries claim: each canonical token and each alias.
 *
 * @param definitions - One layer's list
 * @returns The claimed words
 */
const wordsClaimedBy = (definitions: readonly AliasedDefinition[]): ReadonlySet<string> =>
	new Set(definitions.flatMap((definition) => [definition.canonicalToken, ...definition.aliases]));

/**
 * Every base alias a game overlay's overrides leave behind (D137) — the
 * merge's companion, reading the same two layers under the same
 * "re-declares" rule. Empty when every override carries its base's aliases
 * or the overlay re-declares nothing. The words a standing base entry YIELDS
 * (D226) are {@link findBaseAliasYields}'s.
 *
 * @param baseLexicon - The reference base vocabulary
 * @param overlayLexicon - The game's own vocabulary
 * @returns The drops, directions first, each in overlay order
 */
export const findOverrideAliasDrops = (
	baseLexicon: LexiconDefinition,
	overlayLexicon: LexiconDefinition,
): readonly OverrideAliasDrop[] => [
	...findListAliasDrops('direction', baseLexicon.directions, overlayLexicon.directions),
	...findListAliasDrops('intent', baseLexicon.intents, overlayLexicon.intents),
];

/**
 * Merge one definition list (intents or directions) under the D16 wholesale
 * rule: overridden base entries are replaced in place, new overlay entries
 * append in overlay order — and, by D226, a base entry that stands yields
 * any alias the overlay spells elsewhere.
 *
 * @param baseDefinitions - The base layer's list, defining position for every token it declares
 * @param overlayDefinitions - The overlay layer's list; its entries always win wholesale, its words always win
 * @returns The merged list — deterministic for identical inputs
 */
const mergeDefinitionList = <Definition extends AliasedDefinition>(
	baseDefinitions: readonly Definition[],
	overlayDefinitions: readonly Definition[],
): readonly Definition[] => {
	const overlaidCanonicalTokens = new Set(overlayDefinitions.map((definition) => definition.canonicalToken));
	const overlayWords = wordsClaimedBy(overlayDefinitions);

	const mergedDefinitions: Definition[] = [];

	for (const baseDefinition of baseDefinitions) {
		// D235: a base entry whose NAME the overlay spells as a word of its own
		// yields whole — the pack's reading of the word was there first, and an
		// entry cannot stand without its name.
		if (!overlaidCanonicalTokens.has(baseDefinition.canonicalToken) && overlayWords.has(baseDefinition.canonicalToken)) {
			continue;
		}

		if (overlaidCanonicalTokens.has(baseDefinition.canonicalToken)) {
			// Wholesale in-place override: every overlay entry bearing this token
			// stands where the base entry stood. Valid layers declare each token
			// once, making this a clean 1:1 replacement; a layer-internal
			// duplicate flows through so `compileLexicon` can reject it.
			mergedDefinitions.push(
				...overlayDefinitions.filter((definition) => definition.canonicalToken === baseDefinition.canonicalToken),
			);
		} else {
			// D226: the pack's word outranks the base's — a base alias the
			// overlay spells under another token is the overlay's now.
			mergedDefinitions.push({
				...baseDefinition,
				aliases: baseDefinition.aliases.filter((alias) => !overlayWords.has(alias)),
			});
		}
	}

	const baseCanonicalTokens = new Set(baseDefinitions.map((definition) => definition.canonicalToken));

	for (const overlayDefinition of overlayDefinitions) {
		if (!baseCanonicalTokens.has(overlayDefinition.canonicalToken)) {
			mergedDefinitions.push(overlayDefinition);
		}
	}

	return mergedDefinitions;
};

/**
 * Merge two stopword lists: base order first, overlay additions appended, no
 * duplicates. Returns `undefined` only when NEITHER layer declares the field,
 * preserving the authored absent-vs-empty distinction.
 *
 * @param baseStopwords - The base layer's stopwords, if declared
 * @param overlayStopwords - The overlay layer's stopwords, if declared
 * @returns The unioned stopwords, or `undefined` when neither layer declares any
 */
const mergeStopwords = (
	baseStopwords: undefined | readonly string[],
	overlayStopwords: undefined | readonly string[],
): undefined | readonly string[] => {
	if (baseStopwords === undefined && overlayStopwords === undefined) {
		return undefined;
	}

	return [...new Set([...(baseStopwords ?? []), ...(overlayStopwords ?? [])])];
};

/**
 * Merge a base vocabulary and a game overlay into the ONE `LexiconDefinition`
 * that `compileLexicon` receives (decision D16 — see the module remarks for
 * the full rule set).
 *
 * Pure and non-mutating: identical inputs always produce a deeply equal
 * result, and neither argument is touched.
 *
 * @param baseLexicon - The reference base vocabulary (the layer games extend)
 * @param overlayLexicon - The game's own vocabulary; its declarations win wholesale
 * @returns The merged authored vocabulary, ready for compilation
 */
export const mergeLexiconDefinitions = (
	baseLexicon: LexiconDefinition,
	overlayLexicon: LexiconDefinition,
): LexiconDefinition => {
	const mergedMovementIntentToken = overlayLexicon.movementIntentToken ?? baseLexicon.movementIntentToken;
	const mergedStopwords = mergeStopwords(baseLexicon.stopwords, overlayLexicon.stopwords);
	// D28/D29: word lists union by the same rule as stopwords (lists, not definitions).
	const mergedRecognitionWords = mergeStopwords(baseLexicon.recognitionWords, overlayLexicon.recognitionWords);
	const mergedInterrogativeLeaders = mergeStopwords(baseLexicon.interrogativeLeaders, overlayLexicon.interrogativeLeaders);
	const mergedOrderLeaders = mergeStopwords(baseLexicon.orderLeaders, overlayLexicon.orderLeaders);
	const mergedSocialWords = mergeStopwords(baseLexicon.socialWords, overlayLexicon.socialWords);
	const mergedStatementLeaders = mergeStopwords(baseLexicon.statementLeaders, overlayLexicon.statementLeaders);

	return {
		directions: mergeDefinitionList<DirectionDefinition>(baseLexicon.directions, overlayLexicon.directions),
		intents: mergeDefinitionList<IntentDefinition>(baseLexicon.intents, overlayLexicon.intents),
		...(mergedMovementIntentToken === undefined ? {} : { movementIntentToken: mergedMovementIntentToken }),
		...(mergedInterrogativeLeaders === undefined ? {} : { interrogativeLeaders: mergedInterrogativeLeaders }),
		...(mergedOrderLeaders === undefined ? {} : { orderLeaders: mergedOrderLeaders }),
		...(mergedRecognitionWords === undefined ? {} : { recognitionWords: mergedRecognitionWords }),
		...(mergedSocialWords === undefined ? {} : { socialWords: mergedSocialWords }),
		...(mergedStatementLeaders === undefined ? {} : { statementLeaders: mergedStatementLeaders }),
		...(mergedStopwords === undefined ? {} : { stopwords: mergedStopwords }),
	};
};
